import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Check, ChevronLeft, Trash2 } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { DocumentPreview } from '@/components/DocumentPreview';
import {
  ContactForm,
  emptyContactForm,
  type ContactFormState,
} from '@/components/system/ContactForm';
import { Fonts, Palette, Spacing } from '@/constants/theme';
import type { DocumentRow } from '@/db/types';
import {
  deleteContact,
  getContactById,
  updateContact,
} from '@/lib/contacts';
import { getDocumentById } from '@/lib/documents';
import { goBackOrTo } from '@/lib/nav';

export default function EditContact() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const [form, setForm] = useState<ContactFormState>(emptyContactForm());
  const [working, setWorking] = useState(false);
  const [ready, setReady] = useState(false);
  const [cardDoc, setCardDoc] = useState<DocumentRow | null>(null);
  const [cardOpen, setCardOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      const c = await getContactById(db, id);
      if (cancelled || !c) return;
      setForm({
        name: c.name,
        role: c.role ?? '',
        company: c.company ?? '',
        phone: c.phone ?? '',
        email: c.email ?? '',
        website: c.website ?? '',
        notes: c.notes ?? '',
      });
      if (c.card_doc_id) {
        const doc = await getDocumentById(db, c.card_doc_id);
        if (!cancelled) setCardDoc(doc);
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [db, id]);

  if (!ready) return <View style={styles.container} />;

  const save = async () => {
    if (!form.name.trim() || !id) return;
    setWorking(true);
    try {
      await updateContact(db, id, {
        name: form.name.trim(),
        role: form.role.trim() || null,
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
      });
      goBackOrTo(router, '/contacts');
    } finally {
      setWorking(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    setWorking(true);
    try {
      await deleteContact(db, id);
      goBackOrTo(router, '/contacts');
    } finally {
      setWorking(false);
    }
  };

  const confirmRemove = async () => {
    const ok = await confirm({
      title: 'Remove contact?',
      message: 'This will hide them from your contacts list — task history with them stays.',
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (ok) await remove();
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/contacts')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Edit contact</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
        {cardDoc ? (
          <Pressable
            onPress={() => setCardOpen(true)}
            style={({ pressed }) => [pressed && { opacity: 0.9 }]}>
            <Image
              source={{ uri: cardDoc.file_uri }}
              style={styles.cardImage}
              resizeMode="cover"
            />
            <Text style={styles.cardImageHint}>
              Scanned business card — tap to view, download, or share.
            </Text>
          </Pressable>
        ) : null}

        <View style={styles.card}>
          <ContactForm value={form} onChange={setForm} />
        </View>

        <Pressable
          onPress={save}
          disabled={working}
          style={({ pressed }) => [
            styles.saveBtn,
            working && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{working ? 'Saving…' : 'Save changes'}</Text>
        </Pressable>

        <Pressable onPress={confirmRemove} style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.85 }]}>
          <Trash2 size={16} color={Palette.clay} strokeWidth={1.8} />
          <Text style={styles.removeText}>Remove this contact</Text>
        </Pressable>
      </ScrollView>

      <DocumentPreview doc={cardOpen ? cardDoc : null} onClose={() => setCardOpen(false)} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  cardImage: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    height: 180,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.sandDeep,
  },
  cardImageHint: {
    marginTop: 6,
    marginHorizontal: Spacing.four,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
  },
  card: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 20,
    padding: Spacing.four,
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 16,
    borderRadius: 18,
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  removeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
  },
  removeText: { color: Palette.clay, fontFamily: Fonts.sansBold, fontSize: 13.5 },
});
